import { Component, EventEmitter, OnInit, Input, Output, ViewChild, forwardRef } from '@angular/core';
import { EmailValidator, FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Message, OverlayPanel, SelectItem } from 'primeng/primeng';

import * as ent from '../../entities/index'
import * as enums from '../../enums/index'
import * as serv from '../../services/index'

import { dropDownValidator, emailValidator, emailValidatorWithParameters, phoneNumberValidatorWithParameters, phoneRequiredValidator } from '../../validators/CustomValidators';
import { hiddenDropDownValueValidator } from '../../validators/CustomValidators';

//constants
import { Constants } from '../../constants/Constants';

//baseclasses
import { BaseEditComponent } from '../../baseclasses/BaseEdit.component';
import { ContactDepartmentDropdownComponent } from '../DomainData/ContactDepartmentDropdown.component';
import { ContactFunctionDropdownComponent } from '../DomainData/ContactFunctionDropdown.component';
import { UpdateNeededDropdownComponent } from '../DomainData/UpdateNeededDropdown.component';
import { AddressUtil } from 'src/app/util';

@Component({
    selector: 'my-contact-edit',
    templateUrl: './ContactEdit.component.html',
    providers: [{ provide: BaseEditComponent, useExisting: forwardRef(() => ContactEditComponent) }]
})
export class ContactEditComponent extends BaseEditComponent {

    public displayDialog: boolean;

    contactDetailsForm: FormGroup;
    isAppliesToPayerCheckboxHidden: boolean = true;
    hostedBy: string;

    @Output() public copyOverallAccountPhoneNumberCopied: EventEmitter<boolean> = new EventEmitter();
    @Output() public copyOverallAccountEmailCopied: EventEmitter<boolean> = new EventEmitter();


    @Input() public contactInfo: ent.ContactInfo;

    @ViewChild(ContactDepartmentDropdownComponent) public contactDepartmentDropdownComponent: ContactDepartmentDropdownComponent;
    @ViewChild(ContactFunctionDropdownComponent) public contactFunctionDropdownComponent: ContactFunctionDropdownComponent;
    @ViewChild(UpdateNeededDropdownComponent) public updateNeededDropdownComponent: UpdateNeededDropdownComponent;

    @Input() public showFooter: boolean;
    @Input() public showUpdateNeeded: boolean;
    @Input() public showContactPersonName: boolean;
    @Input() public showContactPhoneNumber: boolean;
    @Input() public showContactPhoneExtension: boolean;
    @Input() public showSecondaryContactName: boolean;
    @Input() public showSecondaryContactPhoneNumber: boolean;
    @Input() public showContactPartnerNumber: boolean;
    @Input() public showAlternateContactPhoneNumber: boolean;
    @Input() public showAlternateContactPhoneExtension: boolean;
    @Input() public showEmail: boolean;
    @Input() public showContactFaxNumber: boolean;
    @Input() public showContactDepartment: boolean;
    @Input() public showContactFunction: boolean;
    @Input() public showAppliesToCustomer: boolean;
    @Input() public showAppliesToPayer: boolean;
    @Input() public showBypassLastNameRequired: boolean;
    @Input() public showCopyOverallAccountPhoneNumber: boolean;
    @Input() public showCopyOverallAccountEmail: boolean;
    @Input() public showAppliesToField: boolean;
    
    @Input() public requireUpdateNeeded: boolean;
    @Input() public requireContactPersonName: boolean;
    @Input() public requireContactPersonLastName: boolean;
    @Input() public requireContactPhoneNumber: boolean;
    @Input() public requireContactPhoneExtension: boolean;
    @Input() public requireSecondaryContactName: boolean;
    @Input() public requireSecondaryContactPhoneNumber: boolean;
    @Input() public requireContactPartnerNumber: boolean;
    @Input() public requireAlternateContactPhoneNumber: boolean;
    @Input() public requireAlternateContactPhoneExtension: boolean;
    @Input() public requireEmail: boolean;
    @Input() public requireContactFaxNumber: boolean;
    @Input() public requireContactDepartment: boolean;
    @Input() public requireContactFunction: boolean;
    @Input() public setContactFunctionValueToDefault: boolean;
    
    contactValidations: ent.ContactValidation[] = [];

    @Output() onOK = new EventEmitter();

    constructor(fb: FormBuilder, private domainDataService: serv.DomainDataService) {
        super();
        let group = {
            'updateNeeded': '',
            'firstName': '',
            'lastName': '',
            'contactPhoneNumber': '',
            'contactPhoneExtension': '',
            'secondaryContactName': '',
            'secondaryContactPhoneNumber': '',
            'contactPartnerNumber': '',
            'alternateContactPhoneNumber': '',
            'alternateContactPhoneExtension': '',
            'email': '',
            'contactFaxNumber': '',
            'contactDepartment': '',
            'contactFunction': '',
            'appliesToCustomer': '',
            'appliesToPayer' : '',
            'bypassLastNameRequired' : '',
            'copyOverallAccountPhoneNumber' : '',
            'copyOverallAccountEmail' : '',
        };

        this.contactDetailsForm = fb.group(group);


    }


    ngOnInit(): void {

        this.contactInfo = new ent.ContactInfo();

        this.domainDataService.getContactValidations({ IncludeInactive: false }).subscribe(x=>{
            this.contactValidations = x;

            this.setPhoneAndEmailValidators();
            
            this.contactDetailsForm.controls['secondaryContactPhoneNumber'].setValidators(phoneNumberValidatorWithParameters(this.contactValidations));
            this.contactDetailsForm.controls['alternateContactPhoneNumber'].setValidators(phoneNumberValidatorWithParameters(this.contactValidations));
            this.contactDetailsForm.controls['contactFaxNumber'].setValidators(phoneNumberValidatorWithParameters(this.contactValidations));

            

            this.contactDetailsForm.controls['contactPhoneNumber'].updateValueAndValidity()
            this.contactDetailsForm.controls['email'].updateValueAndValidity()
        });

        if(this.requireContactPersonName) {
            this.contactDetailsForm.controls['firstName'].setValidators(Validators.required);
        }

        if(this.requireContactPersonLastName) {
            this.contactDetailsForm.controls['lastName'].setValidators(Validators.required);
        }

        if (this.requireUpdateNeeded) {
            this.contactDetailsForm.controls.updateNeeded.setValidators(dropDownValidator);
        }
        else {
            this.contactDetailsForm.controls.updateNeeded.clearValidators();
        }

        if (this.requireContactDepartment) {
            this.contactDetailsForm.controls.contactDepartment.setValidators(dropDownValidator);
        }

        if (this.requireContactFunction) {
            this.contactDetailsForm.controls.contactFunction.setValidators(dropDownValidator);
        }

       

    }

    setPhoneAndEmailValidators() {
        var phoneNumberValidators = [phoneNumberValidatorWithParameters(this.contactValidations)];
        var emailValidators = [emailValidatorWithParameters(this.contactValidations), emailValidator];

        if(this.requireContactPhoneNumber) {
            phoneNumberValidators.push(phoneRequiredValidator);
        }

        if(this.requireEmail) {
            emailValidators.push(Validators.required);
        }
        
        this.contactDetailsForm.controls['contactPhoneNumber'].setValidators(phoneNumberValidators);
        this.contactDetailsForm.controls['email'].setValidators(emailValidators);
    }

    onHide() {
        let contactInfo = new ent.ContactInfo(this.contactInfo);
        this.contactInfo.AppliesToDisplayValue = contactInfo.setAppliesToDisplayValue();
        this.displayDialog = false;
    }

    onOKClick(event: any) {
        let contactInfo = new ent.ContactInfo(this.contactInfo);
        this.contactInfo.AppliesToDisplayValue = contactInfo.setAppliesToDisplayValue();

        this.onOK.emit();
        this.displayDialog = false;

    }

    isFieldValid(fieldName: string){
        var val = true;
        var form = this.contactDetailsForm;
        if (form.controls[fieldName].errors){
            if (form.controls[fieldName].errors.invalid){
                val = false;
            }
        }
        return val;
    }

    getCustomError(fieldName: string) : string{
        
        if (!this.isFieldValid(fieldName)) {
            return "";
        }
        var form = this.contactDetailsForm;
        if (form.controls[fieldName].errors){
            if (form.controls[fieldName].errors.customError){
                return form.controls[fieldName].errors.customError;
            }
        }
        return "";
    }

    contactDepartmentSelected(){
        var val = false;
        var form = this.contactDetailsForm;
        if (form.controls.contactDepartment.valid) {
            val = true;
        }
        return val;
    }

    contactFunctionSelected(){
        var val = false;
        var form = this.contactDetailsForm;
        if (form.controls.contactFunction.valid) {
            val = true;
        }
        return val;
    }

    copyOverallAccountPhoneNumber(checked: boolean){
        this.copyOverallAccountPhoneNumberCopied.emit(checked);
    }

    copyOverallAccountEmail(checked: boolean){
        this.copyOverallAccountEmailCopied.emit(checked);
    }

    bypassLastNameRequired(event: boolean){
        this.setLastNameRequiredValidators(event);   
    }
    
    setLastNameRequiredValidators(enabled: boolean){
        if(enabled){    
            this.contactDetailsForm.controls['lastName'].clearValidators();
            this.contactInfo.LastName = this.contactInfo.FirstName;
        }else{
            if(this.showBypassLastNameRequired) {
                this.contactDetailsForm.controls['lastName'].setValidators(Validators.required);
            }  
            this.contactInfo.LastName = null;            
        }
        this.contactDetailsForm.controls['lastName'].updateValueAndValidity()
    }

    contactDepartmentChange(event: any) {
        this.contactInfo.ContactDepartmentID = event;

        var rec = this.contactDepartmentDropdownComponent.records.filter(x => x.ID == event)[0];
        if (rec) {
            this.contactInfo.ContactDepartmentID = rec.ID;
            this.contactInfo.ContactDepartment.ID = rec.ID;
            this.contactInfo.ContactDepartment.Code = rec.Code;
            this.contactInfo.ContactDepartment.Name = rec.Name;
            this.contactInfo.ContactDepartment.FullName = rec.FullName;
        }

    }

    contactFunctionChange(event: any) {
        this.contactInfo.ContactFunctionID = event;
        var rec = this.contactFunctionDropdownComponent.records.filter(x => x.ID == event)[0];
        if (rec && this.contactInfo && this.contactInfo.ContactFunction) {
            this.contactInfo.ContactFunctionID = rec.ID;
            this.contactInfo.ContactFunction.ID = rec.ID;
            this.contactInfo.ContactFunction.Code = rec.Code;
            this.contactInfo.ContactFunction.Name = rec.Name;
            this.contactInfo.ContactFunction.FullName = rec.FullName;
        }
    }

    updateNeededChange(event: any) {
        this.contactInfo.UpdateNeededID = event;
        var rec = this.updateNeededDropdownComponent.records.filter(x => x.ID == event)[0];
        if (rec) {
            this.contactInfo.UpdateNeeded.ID = rec.ID;
            this.contactInfo.UpdateNeeded.Code = rec.Code;
            this.contactInfo.UpdateNeeded.Name = rec.Name;
            this.contactInfo.UpdateNeeded.FullName = rec.FullName;
        }
        if(this.showUpdateNeeded){
            this.setContactPartnerValidators(event);
        }
    }

    setContactPartnerValidators(updatedNeededID: number) {
        switch (updatedNeededID) {
            default:
                this.setContactFunctionValueToDefault = false;
                this.contactDetailsForm.controls.contactDepartment.clearValidators();
                this.contactDetailsForm.controls.contactFunction.clearValidators();
                this.contactDetailsForm.controls.contactPartnerNumber.clearValidators();
                this.contactDetailsForm.controls.email.setValidators(Validators.required);
                this.contactDetailsForm.controls.contactPhoneNumber.clearValidators();
                if(this.requireContactPersonName) {
                    this.contactDetailsForm.controls['firstName'].setValidators(Validators.required);
                }

                if(this.requireContactPersonLastName) {
                    this.contactDetailsForm.controls['lastName'].setValidators(Validators.required);
                }
                break;
            case enums.UpdateNeededEnum.Delete:
            case enums.UpdateNeededEnum.Update:
                this.contactDetailsForm.controls.contactPartnerNumber.setValidators(dropDownValidator);
                this.contactDetailsForm.controls.contactDepartment.clearValidators();
                this.contactDetailsForm.controls.contactFunction.clearValidators();
                this.contactDetailsForm.controls.email.clearValidators();
                this.contactDetailsForm.controls.lastName.clearValidators();
                this.contactDetailsForm.controls.firstName.clearValidators();
                this.contactDetailsForm.controls.contactPhoneNumber.clearValidators();
                this.contactFunctionDropdownComponent.value = Constants._NONE;    
                break;
            case enums.UpdateNeededEnum.Insert:
                if(this.contactInfo.ContactFunctionID == null || this.contactInfo.ContactFunctionID <= 0){
                    this.setContactFunctionValueToDefault = true;
                    this.contactFunctionDropdownComponent.value = Constants._DEFAULT_CONTACT_FUNCTION_ID;    
                    this.contactFunctionChange(Constants._DEFAULT_CONTACT_FUNCTION_ID);
                } else {
                    this.setContactFunctionValueToDefault = false;
                }
            
                this.contactInfo.ContactPartnerNumber = '';
                this.contactDetailsForm.controls.contactPartnerNumber.clearValidators();
                        
                this.contactDetailsForm.controls.contactDepartment.setValidators(dropDownValidator);
                this.contactDetailsForm.controls.contactFunction.setValidators(dropDownValidator);
                this.setPhoneAndEmailValidators();

                if(this.requireContactPersonName) {
                    this.contactDetailsForm.controls['firstName'].setValidators(Validators.required);
                }

                if(this.requireContactPersonLastName) {
                    this.contactDetailsForm.controls['lastName'].setValidators(Validators.required);
                }
                
        }
        this.contactDetailsForm.controls.contactPartnerNumber.updateValueAndValidity()
        this.contactDetailsForm.controls.contactDepartment.updateValueAndValidity();
        this.contactDetailsForm.controls.contactFunction.updateValueAndValidity();
        this.contactDetailsForm.controls.email.updateValueAndValidity();
        this.contactDetailsForm.controls.lastName.updateValueAndValidity();
        this.contactDetailsForm.controls.firstName.updateValueAndValidity();
        this.contactDetailsForm.controls.contactPhoneNumber.updateValueAndValidity();
    }

    disableContactPartnerNumber(): string {
        switch (this.contactInfo.UpdateNeededID) {
            case enums.UpdateNeededEnum.Insert:
                return 'disabled';
            default:
                return null;
        }
    }

    disableFields(): string {
        switch (this.contactInfo.UpdateNeededID) {
            case enums.UpdateNeededEnum.Delete:
                return 'disabled';
            default:
                return null;
        }
    }

    disableControls(): boolean {
        switch (this.contactInfo.UpdateNeededID) {
            case enums.UpdateNeededEnum.Delete:
                return true;
            default:
                return false;
        }
    }

    hideAppliesToPayerCheckbox(val : boolean){ 
        this.isAppliesToPayerCheckboxHidden = val;
    }
}